<script context="module">
  export const selector = ".find-service-form__widget";
</script>

<script lang="ts">
  import { onMount } from "svelte";
  import Litepicker from "litepicker";
  import LocationTypeahead from "../LocationTypeahead/LocationTypeahead.svelte";
  import type { SearchAddressResult } from "../../../typings/Location";
  import Tab from "bootstrap/js/src/tab";
  import Modal from "bootstrap/js/src/modal";
  import { userStore } from "../stores/UserStore";
  import {
    fetchIsExistingCustomer,
    fetchIsServiceTypeAvailable,
    fetchPricingResults,
    fetchPricingUpdate,
    fetchServiceTypesByZipcode,
    formatCardPrice,
    postPricingSubmit,
  } from "./FindServiceForm";
  import Select from "svelte-select/Select.svelte";
  import type {
    BusinessType,
    Frequency,
    PricingSearchRequest,
    PricingSearchResponse,
    PricingSearchResult,
    ServiceType,
    WasteType,
    GetAQuoteQueryString,
    GAQPricingSubmitRequest,
    Quantity,
  } from "../../../typings/GetAQuote";
  import AtomicIconDropdown from "../Icons/SvelteSelectChevronIcon.svelte";
  import FacetFilterIcon from "../Icons/FacetFilterIcon.svelte";
  import SearchStatusMessage from "./blocks/SearchStatusMessage.svelte";
  import GAQFacetList from "./blocks/GAQFacetList.svelte";
  import queryString from "query-string";
  import { hasFacetsOpen } from "../stores/GAQStore";
  import { isMobileViewport } from "../../utilities/ViewportHelpers";
  import { formatLitePickerDateRange } from "../../utilities/StringHelpers";
  import type {RequestAQuoteFunnelEventType} from "../../components/GTM";
  import type {CellMouseDownEvent} from "@ag-grid-community/core";

  export let searchPlaceholder: string = "";
  export let searchButtonLabel: string = "";
  export let nextButtonLabel: string = "";
  export let searchRedirect: string = "";
  export let searchTitle: string = "";
  export let searchDescription: string = "";
  export let invalidAddressMessage: string = "";
  export let containerDetailsTitle: string = "";
  export let editLabel: string = "";
  export let searchSubTitle: string = "";
  export let tabOneLabel: string = "";
  export let tabTwoLabel: string = "";
  export let tabThreeLabel: string = "";
  export let serviceTypeIdFromPage: number = 0; // only provided when on service type page or child page
  export let mobileFilterServicesLabel: string = "";
  export let apiGetSearchAddress: string = "";
  export let disablePrecheck: boolean = false;
  export let apiGetSearchAddressReverse: string = "";
  export let apiExistingCustomer: string = "";
  export let apiServiceTypes: string = "";
  export let apiGetAllServiceTypesProxy: string = "";
  export let apiPricingSearch: string = "";
  export let apiPricingSubmit: string = "";
  export let submissionSuccessModalId: string = "";
  export let selectServiceTitle: string = "";
  export let businessTypeTitle: string = "";
  export let preSearchIconSrc: string = "";
  export let preSearchTitle: string = "";
  export let preSearchDescription: string = "";
  export let quotePageUrl:string=""

  // general form
  let findServiceFormEl: HTMLElement = null;
  let isInEditMode: boolean = true;
  let isLoading: boolean = false;
  let isFirstFocus: boolean = true;

  // location
  const { userAddress, userGeoCoords } = userStore;

  let isInputClear: boolean;
  let selectedResult: SearchAddressResult;
  let isLocationInputValid: boolean = true;
  let locationTypeaheadChild: LocationTypeahead;

  //date ranges: roll-off delivery & pickup dates
  let litePickerRolloff: Litepicker = null;
  let litePickerRollOffEl: HTMLInputElement = null;
  let litePickerRolloff_SelectedDatesFormatted: string = "";
  $: if (!isContainerDetailsNextValid) {
    litePickerRolloff_SelectedDatesFormatted = ""; // reset roll-off dates to empty
  }

  // service types and look ups
  let serviceTypesByZip: ServiceType[] = null;
  let selectedServiceTypeByZip: ServiceType = null;
  let selectedWasteType: WasteType = null;
  let selectedFrequency: Frequency = null;
  let selectedBusinessType: BusinessType = null;

  // results and modals
  let pricingSearchResponse: PricingSearchResponse = null;
  let searchStatusMessageHidden: boolean = true;
  let noResultsContainerEl: HTMLElement = null;
  let noServiceTypesMessageBlockEl: HTMLElement = null;
  let apiErrorMessageBlockEl: HTMLElement = null;

  // pricing submit modal
  let pricingSubmitModalEl: HTMLElement = null;
  let pricingSubmitFormEl: HTMLFormElement = null;
  let pricingSubmitModal: Modal = null;
  let selectedServiceResult: PricingSearchResult = null;
  let selectedQuantity: Quantity = null;
  let litePickerDeliveryDate: Litepicker = null;
  let litePickerDeliveryDateEl: HTMLInputElement = null;
  let otherServicesCheckboxValue: boolean = false;
  let pricingModalServerSideErrorMessage: string = "";

  let pricingFormInputName: string = "";
  let pricingFormInputEmail: string = "";
  let pricingFormInputPhone: string = "";
  let pricingFormInputCouponCode: string = "";
  let formFieldEngage = [];

  $: isSubmitDisabled = isLoading;

  $: {
    if (pricingSubmitFormEl) {
      for (const field of pricingSubmitFormEl.elements) {
        field.addEventListener("focus", function (evt) {
          if (isFirstFocus) {
            dispatchGTMFormStartEvent(
              'formStart',
              field,
            );
          }

          isFirstFocus = false;
        });
      }

      getFieldsEngagement(pricingSubmitFormEl);
    }
  }

  // whenever selected service changes
  $: if (selectedServiceTypeByZip != null) {
    // initialize or clear date picker when date range is enabled within service type
    if (selectedServiceTypeByZip.enableDateRange === true && litePickerRollOffEl != null && litePickerRolloff == null) {
      initDatePicker();
    } else if (litePickerDeliveryDateEl != null && selectedServiceResult != null) {
      initDatePicker(); // initialize (or re-initialize) delivery date picker in modal
    }
  }

  // pricing submit modal: whenever quantity updates and selected service has a price, update pricing
  function updatePricingInModal(event, overrideQuantity: Quantity) {
    let tempQuantity = overrideQuantity || (event.detail as Quantity);

    if (tempQuantity != null && parseInt(tempQuantity.value) > 0 && selectedServiceResult !== null && selectedServiceResult.price !== null) {
      fetchPricingUpdate("/api/proxy/pricing/v1/commercial/price", {
        gatelock: false,
        businesstypeid: selectedBusinessType?.businessTypeId,
        loadtypeid: selectedServiceResult.loadTypeId,
        containersizeid: selectedServiceResult.containerSizeId,
        containercount: parseInt(tempQuantity.value),
        servicefrequencyid: selectedServiceResult?.frequency?.serviceFrequencyId,
        materialtypeid: selectedServiceResult?.wasteType?.materialTypeId,
        postalcode: $userAddress?.address?.postalCode,
        latitude: $userGeoCoords?.latitude,
        longitude: $userGeoCoords?.longitude,
        department: "ecommerce"
      }).then((response) => {
        selectedServiceResult.price = response.suggestedRate;
      });
    }
  }

  // pricing submit success modal
  let pricingSubmitSuccessModal: Modal = null;

  // existing customer check
  let isExistingCustomer: boolean = false; // note: hide prices when true
  $: if (
    selectedResult &&
    selectedResult.address &&
    apiExistingCustomer.length &&
    selectedResult.address.streetNumber &&
    selectedResult.address.streetName
  ) {
    fetchIsExistingCustomer(apiExistingCustomer, {
      address: `${selectedResult.address.streetNumber} ${selectedResult.address.streetName}`,
      city: (selectedResult.address.localName && selectedResult.address.localName.length) ? selectedResult.address.localName : selectedResult.address.municipality,
      state: selectedResult.address.countrySubdivision,
      zip: selectedResult.address.postalCode,
    })
      .then((result) => {
        isExistingCustomer = result.status === 200;
      })
      .catch((_) => {
        toggleResultMessages(true, true);
      });
  }

  // TABS
  let locationTabEl: HTMLElement;
  let serviceTabEl: HTMLElement;
  let containerDetailsTabEl: HTMLElement;
  let shownTabEl: HTMLElement = null;
  let isContainerDetailsNextValid: boolean = false;
  $: isContainerDetailsTabHidden = selectedServiceTypeByZip != null && !selectedServiceTypeByZip.wasteTypes.length;

  // FACETS
  let selectedFacetValueIds: string[] = [];
  let hasFacets: boolean = false;
  hasFacetsOpen.subscribe((isOpen) => {
    isOpen ? document.body.classList.add("facets-open") : document.body.classList.remove("facets-open");
  });

  // ALL FUNCTIONS
  function retrieveValidityUpdate(e) {
    isLocationInputValid = e.detail.isValid;
  }

  const getFieldsEngagement = (form) => {

    return new Promise((resolve, reject) => {

      for (const field of form.elements) {
        const fieldSet = field.closest(".input-group") || field.closest(".selection-wrapper--float-label") || field.closest(".form-check");
        if (fieldSet && field.type !== "checkbox") {
          const label = fieldSet.querySelector("label");
          let labelText = "";
          if (label) labelText = label.textContent.trim();
          if (labelText) {
            const isLabelUnique = !formFieldEngage.some(item => Object.keys(item)[0] === labelText);

            let labelEngagement = 'not-engaged';
            if (selectedServiceTypeByZip.enableDateRange && labelText == "Requested Delivery Date") {
              labelEngagement = null;
            }

            if (isLabelUnique) {
              formFieldEngage.push(
                {
                  [labelText]: labelEngagement,
                }
              )
            }
          }

          field.addEventListener("change", function () {
            if (field.value !== "") {
              const label = fieldSet.querySelector("label");
              let labelText = "";
              if (label) labelText = label.textContent.trim();

              if (labelText) {
                const index = formFieldEngage.findIndex(item => Object.keys(item)[0] === labelText);

                if (index !== -1) {
                  formFieldEngage[index][labelText] = 'engaged';
                }
              }
            }
          });
        }
      }

      resolve();
    });
  }

  function dispatchGTMFormFieldEngagementEvent(event, form) {
    const args = {
      event,
      formFieldEngage: formFieldEngage.map(function(item) { return `${Object.keys(item)[0]} - ${Object.values(item)[0]}`}),
      formName: document.querySelector("#pricingSubmitLabel").textContent.trim(),
      formId: form.id,
    };

    window.dataLayer.push(args);
  }

  function handleClickBeforeRedirect(e) {
    e.preventDefault();

    if (isInputClear) {
      locationTypeaheadChild.showErrorMessageExternal();
      return false;
    }
    const parsedUrl = queryString.parseUrl(searchRedirect);

    if (disablePrecheck) {
      parsedUrl.query.postalCode = $userAddress.address.postalCode;
      window.location.href = queryString.stringifyUrl(parsedUrl);
    } else {
      // check if service type is available first before redirecting
      fetchIsServiceTypeAvailable(apiGetAllServiceTypesProxy, $userAddress.address.postalCode, serviceTypeIdFromPage).then((isAvailable) => {
        if (isAvailable) {
          // if serviceTypeIdFromPage availableredirect with serviceTypeId to GAQ, else redirect with postal code
          if (serviceTypeIdFromPage > 0) {
            parsedUrl.query.serviceTypeId = serviceTypeIdFromPage.toString();
          } else {
            parsedUrl.query.postalCode = $userAddress.address.postalCode;
          }
          window.location.href = queryString.stringifyUrl(parsedUrl);
        } else {
          if (serviceTypeIdFromPage > 0) {
            // toggle error message
            toggleResultMessages(true, false);
          } else {
            // on 'Find Service Near You', reload page to hide cards from backend
            parsedUrl.query.postalCode = $userAddress.address.postalCode;
            window.location.href = queryString.stringifyUrl(parsedUrl);
          }
        }
      });
    }
  }

  function precheckPostalCode() {
    const parsedUrl = queryString.parseUrl(window.location.href);
    if (parsedUrl.query.postalCode) {
      fetchIsServiceTypeAvailable(apiGetAllServiceTypesProxy, $userAddress.address.postalCode, serviceTypeIdFromPage).then((isAvailable) => {
        if (!isAvailable) {
          toggleResultMessages(true, false);
        }
      });
    }
  }

  function initializeTabs() {
    var triggerTabList = [].slice.call(document.querySelectorAll("#findServiceFormTabs button[role='tab']"));
    triggerTabList.forEach(function (triggerEl) {
      new Tab(triggerEl);
      triggerEl.addEventListener("click", function (event) {
        event.preventDefault();
        return false;
      });
      triggerEl.addEventListener("shown.bs.tab", function (event) {
        shownTabEl = event.target; // newly activated tab
        //event.relatedTarget; // previous active tab
      });
    });
  }

  function initializeModals() {
    if (pricingSubmitModalEl != null) {
      pricingSubmitModal = new Modal(pricingSubmitModalEl);
    }

    if (document.getElementById(submissionSuccessModalId) != null) {
      pricingSubmitSuccessModal = new Modal(document.getElementById(submissionSuccessModalId));
    }
  }

  function initializeElements() {
    noResultsContainerEl = document.getElementById("gaqNoResultsContainer");
    noServiceTypesMessageBlockEl = document.querySelector(".gaq__no-service-types-message");
    apiErrorMessageBlockEl = document.querySelector(".gaq__service-down");
  }

  function handleClickLocationNext(preSelectedServiceTypeId = "") {
    if (isLocationInputValid) {
      fetchServiceTypesByZipcode(apiServiceTypes,
        $userAddress.address.postalCode,
        $userGeoCoords.latitude || null,
        $userGeoCoords.longitude || null,
      )
        .then((serviceTypesResponse) => {
          if (serviceTypesResponse.status === 204) {
            // if status === 204, show "no services available" block
            toggleResultMessages(true, false);
          } else {
            toggleResultMessages(false, false);
            dispatchRequestAQuoteFunnelEngagement({
              event: 'requestAQuote',
              quoteEngagement: 'Progress',
              quoteStep: 'Check Local Availability',
              quoteStepNumber: '1',
              state: selectedResult?.address.countrySubdivision || null,
              zip: selectedResult?.address.postalCode || null,
              service: null,
              businessType: null,
              wasteType: null,
              frequency: null,
            });

            serviceTypesByZip = serviceTypesResponse.serviceTypes;
            Tab.getInstance(serviceTabEl).show();

            // if pre-selecting the service type (by query string)
            if (preSelectedServiceTypeId.length) {
              const preSelectedServiceType = serviceTypesByZip.find((s) => {
                return s.serviceTypeId === parseInt(preSelectedServiceTypeId);
              });
              handleClickSelectService(preSelectedServiceType);

              const hasBusinessTypes = selectedServiceTypeByZip?.businessTypes?.length;
              if (!hasBusinessTypes) {
                // only move to next state (tab 3 OR search) if service type doesn't have business type
                setTimeout(() => {
                  handleClickSelectServiceNext();
                }, 200);
              }
            }
          }
        })
        .catch((_) => {
          toggleResultMessages(true, true);
        });
    }
  }

  function dispatchGTMFindServiceEvent() {
    if ($userAddress) {
        const args = {
          event: "findService",
          state:  $userAddress && $userAddress.address && $userAddress.address.countrySubdivision || null,
          zip: $userAddress && $userAddress.address && $userAddress.address.postalCode || null,
        };

        window.dataLayer.push(args);
    }
  }

  function handleClickSelectService(serviceType: ServiceType) {
    searchStatusMessageHidden = true;

    // deselect all and set selected one
    serviceTypesByZip = serviceTypesByZip.map((s) => {
      return {
        ...s,
        isSelected: s.serviceTypeId === serviceType.serviceTypeId,
      };
    });
    selectedServiceTypeByZip = serviceType;

    selectedQuantity = {
      label: selectedServiceTypeByZip.defaultContainerQuantity.toString(),
      value: selectedServiceTypeByZip.defaultContainerQuantity.toString(),
    };

    // load lookup data into dropdowns
    if (serviceType.businessTypes) {
       const _selectedBusinessType = serviceType.businessTypes.find((b) => b.default) || serviceType.businessTypes[0];
       selectedBusinessType = _selectedBusinessType ? {
        ..._selectedBusinessType,
        label: _selectedBusinessType.name,
        value: _selectedBusinessType.businessTypeId
       } : _selectedBusinessType;
    }
  }

  function handleClickSelectServiceNext() {
    if (isContainerDetailsTabHidden) {
      handleSearch();
    } else {
      // show container details tab
      Tab.getInstance(containerDetailsTabEl).show();
    }
    dispatchRequestAQuoteFunnelEngagement({
        event: 'requestAQuote',
        quoteEngagement: 'Progress',
        quoteStep: 'Select Service',
        quoteStepNumber: '2',
        state: selectedResult?.address.countrySubdivision || null,
        zip: selectedResult?.address.postalCode || null,
        service: selectedServiceTypeByZip?.name || null,
        businessType: selectedBusinessType?.name || null,
        wasteType: null,
        frequency: null,
      });
  }

  function handleClickContainerDetailsNext() {
    handleSearch();
  }

  function initDatePicker() {
    const tempDate = new Date();

    // roll-off date range picker
    if (litePickerRolloff === null && litePickerRollOffEl !== null) {
      isContainerDetailsNextValid = false;

      litePickerRolloff = new Litepicker({
        element: litePickerRollOffEl,
        singleMode: false,
        numberOfColumns: 2,
        numberOfMonths: 2,
        minDate: new Date().getTime() + 48 * 60 * 60 * 1000, // min 48 hours from now
        maxDate: new Date(tempDate.setMonth(tempDate.getMonth() + 2)), // max 2 months out
        format: "MM/DD/YYYY",
        setup: (picker) => {
          picker.on("selected", (date1, date2) => {
            isContainerDetailsNextValid = true;
            litePickerRolloff_SelectedDatesFormatted = formatLitePickerDateRange(date1, date2);

            if (process.env.dev) {
              console.log(date1);
              console.log(date2);
            }
          });
        },
      });
    } else if (litePickerRollOffEl !== null && isInEditMode) {
      // clear calendar
      isContainerDetailsNextValid = false;
      litePickerRolloff.clearSelection();
    }

    // pricing submit modal delivery date picker
    if (litePickerDeliveryDate === null && litePickerDeliveryDateEl !== null) {
      litePickerDeliveryDate = new Litepicker({
        element: litePickerDeliveryDateEl,
        parentEl: litePickerDeliveryDateEl.parentElement,
        singleMode: true,
        position: "top left",
        minDate: new Date().getTime() + 48 * 60 * 60 * 1000, // min 48 hours from now
        maxDate: new Date(tempDate.setMonth(tempDate.getMonth() + 2)), // max 2 months out
        setup: (picker) => {
          picker.on("selected", (date1, date2) => {
            const labelText = "Requested Delivery Date";
            const index = formFieldEngage.findIndex(item => Object.keys(item)[0] === labelText);

            if (index !== -1) {
              formFieldEngage[index][labelText] = 'engaged';
            }
          });
        }
      });
    }
  }

  function handleSearch() {
    const queryParams: PricingSearchRequest = {
      postalCode: $userAddress.address.postalCode,
      city: ($userAddress.address.localName && $userAddress.address.localName.length) ? $userAddress.address.localName : $userAddress.address.municipality,
      serviceTypeId: selectedServiceTypeByZip.serviceTypeId,
      businessTypeId: selectedBusinessType?.businessTypeId,
      materialTypeId: selectedWasteType?.materialTypeId,
      serviceFrequencyId: selectedFrequency?.serviceFrequencyId,
      latitude: $userGeoCoords.latitude,
      longitude: $userGeoCoords.longitude,
      numberOfDays: null,
      isExistingCustomer: isExistingCustomer,
    };

    // include number of days if roll-off
    if (litePickerRolloff !== null && litePickerRolloff.getDate()) {
      let numberOfDays = 1;
      if (litePickerRolloff.getEndDate() !== litePickerRolloff.getStartDate()) {
        numberOfDays = (litePickerRolloff.getEndDate().getTime() - litePickerRolloff.getStartDate().getTime()) / (1000 * 3600 * 24);
      }
      queryParams.numberOfDays = numberOfDays;
    }

    fetchPricingResults(apiPricingSearch, queryParams)
      .then((response) => {
        if (response.status == 200) {
          pricingSearchResponse = response;
          searchStatusMessageHidden = false;
          isInEditMode = false;

          dispatchRequestAQuoteFunnelEngagement({
            event: 'requestAQuote',
            quoteEngagement: 'Select A Container',
            quoteStep: 'Select A Container',
            quoteStepNumber: '3',
            state: selectedResult?.address.countrySubdivision || null,
            zip: selectedResult?.address.postalCode || null,
            service: selectedServiceTypeByZip?.name || null,
            businessType: selectedBusinessType?.name || null,
            wasteType: selectedWasteType?.name || null,
            frequency: selectedFrequency?.display || null,
          });
        } else {
          toggleResultMessages(true, false);
        }
      })
      .catch((_) => {
        toggleResultMessages(true, true);
      });
  }

  function changeEditModeAndTab(tabEl: HTMLElement, step: number) {
    isInEditMode = true;
    Tab.getInstance(tabEl).show();
    findServiceFormEl.scrollIntoView();
    pricingSearchResponse = null;
    selectedServiceResult = null;

    // in order to re-initialize datepicker when modal in DOM comes back
    if (litePickerDeliveryDate !== null) {
      litePickerDeliveryDate.destroy();
      litePickerDeliveryDate = null;
    }

    if (step < 3) {
      selectedWasteType = null;
      selectedFrequency = null;
      selectedBusinessType = selectedServiceTypeByZip !== null ? selectedServiceTypeByZip.businessTypes.find((b) => b.default) : null;

      if (litePickerRolloff !== null) {
        isContainerDetailsNextValid = false;
        litePickerRolloff.clearSelection();
      }
    }
    if (step === 1) {
      // unselect all and remove selected instance
      serviceTypesByZip = serviceTypesByZip.map((s) => {
        return {
          ...s,
          isSelected: false,
        };
      });
      selectedServiceTypeByZip = null;
    }
  }

  function restartFindService() {
    pricingSubmitModal.hide();
    changeEditModeAndTab(locationTabEl, 1);
  }

  function dispatchGTMFormStartEvent(event, field) {
    let formName = null;
    const formTitle = field.closest('#pricingSubmitModal').querySelector("#pricingSubmitLabel");
    if (formTitle) formName = formTitle.textContent.trim();

    let formFieldName = null;
    const formField = field.parentNode.querySelector("label");
    if (formField) formFieldName = formField.textContent.trim();

    const args = {
      event,
      formFieldName,
      formName,
      formID: field.closest('form').id,
    };

    window.dataLayer.push(args);
  }

  function handleClickSelectServiceFromCard(result: PricingSearchResult) {
    selectedServiceResult = result;
    if (selectedServiceTypeByZip.maxContainerQuantity > 0) {
      updatePricingInModal(null, selectedQuantity);
    }
    pricingSubmitModal.show();

    dispatchRequestAQuoteFunnelEngagement({
          event: 'requestAQuote',
          quoteEngagement: 'Completed',
          quoteStep: 'Select Service',
          quoteStepNumber: '4',
          state: selectedResult?.address.countrySubdivision || null,
          zip: selectedResult?.address.postalCode || null,
          service: selectedServiceTypeByZip?.name || null,
          businessType: selectedBusinessType?.name || null,
          wasteType: selectedWasteType?.name || null,
          frequency: selectedFrequency?.display || null,
        });
  }

  function dispatchGTMServiceCtaLinkEvent() {
    dispatchRequestAQuoteFunnelEngagement({
          event: 'requestAQuote',
          quoteEngagement: 'Completed',
          quoteStep: 'Select Service',
          quoteStepNumber: '4',
          state: selectedResult?.address.countrySubdivision || null,
          zip: selectedResult?.address.postalCode || null,
          service: selectedServiceTypeByZip?.name || null,
          businessType: selectedBusinessType?.name || null,
          wasteType: selectedWasteType?.name || null,
          frequency: selectedFrequency?.display || null,
        });
  }

  interface DispatchGTMRequestAQuoteCompleteType {
    event: string,
    quoteEngagement: string,
    businessType: string,
    wasteType: string,
    frequency: string,
    couponCode: string | null,
    quantity: string,
    monthlyServicePrice: string,
    productName: string,
    additionalServices: string | null,
  }

  function dispatchGTMRequestAQuoteComplete (detail: DispatchGTMRequestAQuoteCompleteType) {
    window.dataLayer.push(detail);
  }

  function dispatchGTMFormCompleteEvent (event, form) {
    const args = {
      event,
      formName: document.querySelector("#pricingSubmitLabel").textContent.trim(),
      formId: form.id,
    };

    window.dataLayer.push(args);
  }

  function handleSubmitPricingModal(e) {
    // check form validation
    const form = e.currentTarget as HTMLFormElement;
    form.classList.add("was-validated");
    e.preventDefault();
    e.stopPropagation();

    if (!form.checkValidity()) {
      return false;
    }

    // gather post request body
    const postBodyObj = {
      name: pricingFormInputName,
      email: pricingFormInputEmail,
      phone: pricingFormInputPhone,
      couponCode: pricingFormInputCouponCode,
      businessType: selectedBusinessType?.name,
      wasteType: selectedServiceResult.wasteType?.description,
      frequency: selectedServiceResult.frequency?.display,
      quantity: selectedQuantity?.value,
      requestedDeliveryDate: litePickerDeliveryDate?.getStartDate()?.toDateString(),
      selectedAddress: selectedResult?.address?.freeformAddress,
      interestedInOtherServices: otherServicesCheckboxValue,
      quotedPrice: selectedServiceResult.price,
      containerSize: selectedServiceResult.title,
      existingCustomer: isExistingCustomer,
      serviceType: selectedServiceTypeByZip.description,
      quotePageUrl:quotePageUrl

    } as GAQPricingSubmitRequest;

    if (litePickerRolloff.getStartDate() !== null) {
      postBodyObj.rollOffDateRange = `${litePickerRolloff?.getStartDate()?.toDateString()} - ${litePickerRolloff?.getEndDate()?.toDateString()}`;
    }

    isLoading = true;
    // submit POST
    postPricingSubmit(apiPricingSubmit, postBodyObj)
      .then((response) => {
        if (response.status === 204) {
          pricingSubmitModal.hide();
          pricingModalServerSideErrorMessage = "";

            dispatchGTMRequestAQuoteComplete(
              {
                event: 'requestAQuoteComplete',
                quoteEngagement: 'Complete',
                businessType: selectedBusinessType?.name || null,
                wasteType: selectedWasteType?.description || null,
                frequency: selectedServiceResult?.frequency?.display || null,
                couponCode: pricingFormInputCouponCode || null,
                quantity: (selectedQuantity?.value ?? 0) == 0 ? "1" : selectedQuantity.label,
                monthlyServicePrice: selectedServiceResult?.price ?`$${selectedServiceResult.price}` : "Pricing will be determined by your site location and existing services",
                productName: selectedServiceResult?.title,
                additionalServices: otherServicesCheckboxValue ? "Opt-In" : "Opt-out",
              }
            );
            dispatchGTMFormCompleteEvent("formComplete", form);
            dispatchGTMFormFieldEngagementEvent("formFieldEngagement", form);
          pricingSubmitSuccessModal.show();

        } else {
          pricingModalServerSideErrorMessage = response.errorMessage;
        }
      })
      .catch((e) => {
        pricingModalServerSideErrorMessage = e.toString();
        return false;
      })
      .finally(() => {
        isLoading = false;
      });
  }

  function formatProductPricingLabel(pricingLabel: string, result: PricingSearchResult) {
    return pricingLabel.replace("[frequencyUnit]", result.frequencyUnit);
  }
  function formatProductPricingDescription(pricingDescription: string, result: PricingSearchResult) {
    if (!result.weightIncluded || !result.overageCharge) return pricingDescription;
    
    return pricingDescription.replace("[tonsQuantity]", result.weightIncluded.toFixed(2)).replace("[overageCharge]", result.overageCharge.toFixed(2));
  }

  function initializeBypass() {
    // parse serviceTypeId from query string
    const qsObject = queryString.parse(location.search) as GetAQuoteQueryString;
    if (qsObject.serviceTypeId && qsObject.serviceTypeId.length) {
      handleClickLocationNext(qsObject.serviceTypeId);
    }
  }

  function dispatchRequestAQuoteFunnelEngagement(detail: RequestAQuoteFunnelEventType) {
    const requestAQuoteFunnelEngagementEvent = new CustomEvent('requestAQuote', {
      detail,
    });

    document.dispatchEvent(requestAQuoteFunnelEngagementEvent);
  }

  function handleWasteTypeChange(event) {
    event.preventDefault();
    selectedFrequency = null;
  }

  function handleFrequencyChange(event) {
    selectedFrequency = event.detail;
  }

  function toggleResultMessages(isShowing = true, isApiError = false) {
    if (isShowing) {
      // serviceTypesByZip = []; // change from null state, empty array means we sent a request
      noResultsContainerEl.style.display = "block";
      if (isApiError) {
        apiErrorMessageBlockEl.style.display = "block";
        noServiceTypesMessageBlockEl.style.display = "none";
        dispatchRequestAQuoteFunnelEngagement({
          event: 'requestAQuote',
          quoteEngagement: 'Unavailable',
          quoteStep: 'Sorry, we are unable to get a quote',
          quoteStepNumber: '4',
          state: selectedResult?.address.countrySubdivision || null,
          zip: selectedResult?.address.postalCode || null,
          service: selectedServiceTypeByZip?.name || null,
          businessType: selectedBusinessType?.name || null,
          wasteType: selectedWasteType?.name || null,
          frequency: selectedFrequency?.display || null,
        });
      } else {
        apiErrorMessageBlockEl.style.display = "none";
        noServiceTypesMessageBlockEl.style.display = "block";

        dispatchRequestAQuoteFunnelEngagement({
          event: 'requestAQuote',
          quoteEngagement: 'Unavailable',
          quoteStep: 'Sorry, we are unable to get a quote',
          quoteStepNumber: '4',
          state: selectedResult?.address.countrySubdivision || null,
          zip: selectedResult?.address.postalCode || null,
          service: null,
          businessType: null,
          wasteType: null,
          frequency: null,
        });
      }
    } else {
      noResultsContainerEl.style.display = "none";
      noServiceTypesMessageBlockEl.style.display = "none";
      apiErrorMessageBlockEl.style.display = "none";
    }
  }

  onMount(async () => {
    initializeTabs();
    initializeModals();
    initializeElements();
    precheckPostalCode();
    initializeBypass();
  });
</script>

<div
  class="find-service-form"
  bind:this={findServiceFormEl}
  class:facets-open={$hasFacetsOpen}
  class:edit-mode={isInEditMode}
  class:find-service-form__dynamic={!searchRedirect.length}
>
  <div class="container">
    <h2 class="find-service-form__title">{searchTitle}</h2>
    {#if searchDescription.length}
      <p class="find-service-form__description">{searchDescription}</p>
    {/if}

    {#if searchRedirect.length}
      <div class="find-service-form__search-wrapper">
        <LocationTypeahead
          bind:isInputClear
          bind:this={locationTypeaheadChild}
          placeholder={searchPlaceholder}
          {invalidAddressMessage}
          {apiGetSearchAddress}
          {apiGetSearchAddressReverse}
          labelFloatInside={false}
          bind:selectedSearchAddressResult={selectedResult}
          on:validChange={retrieveValidityUpdate}
        />
        {#if searchRedirect.length}
          <div class="find-service-form__search-btn-wrapper">
            <a
              href={searchRedirect}
              class="btn btn-primary find-service-form__search-btn"
              class:disabled={!isLocationInputValid}
              on:click={handleClickBeforeRedirect}
              on:mousedown={dispatchGTMFindServiceEvent}
            >
              {searchButtonLabel}</a
            >
          </div>
        {/if}
      </div>
    {:else}
      <ul class="nav nav-tabs nav-tabs--primary nav-tabs--manual" id="findServiceFormTabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            bind:this={locationTabEl}
            class="nav-link active"
            id="tab1"
            data-bs-target="#tabPane1"
            type="button"
            role="tab"
            aria-controls="tabPane1"
            aria-selected="true"
            ><span class="step-circle">1<span class="step-circle-dot">.</span></span>
            {tabOneLabel}
          </button>
          {#if shownTabEl !== null && (!isInEditMode || (locationTabEl && !locationTabEl.classList.contains("active")))}
            <button class="btn-edit btn-reset text-primary" on:click={() => changeEditModeAndTab(locationTabEl, 1)}>{editLabel} </button>
          {/if}

          {#if $userAddress?.address}
            <div class="selected-tab-data mt-2">
              <span>{$userAddress.address.freeformAddress}</span>
            </div>
          {/if}
        </li>
        <li class="nav-item" role="presentation">
          <button
            bind:this={serviceTabEl}
            class="nav-link"
            id="tab2"
            data-bs-target="#tabPane2"
            type="button"
            role="tab"
            aria-controls="tabPane2"
            aria-selected="false"
            ><span class="step-circle">2<span class="step-circle-dot">.</span></span>
            {tabTwoLabel}
          </button>
          {#if shownTabEl !== null && (!isInEditMode || (containerDetailsTabEl && containerDetailsTabEl.classList.contains("active")))}
            <button class="btn-edit btn-reset text-primary" on:click={() => changeEditModeAndTab(serviceTabEl, 2)}>{editLabel}</button>
          {/if}
          {#if selectedServiceTypeByZip}
            <div class="selected-tab-data mt-2">
              <span>
                {selectedServiceTypeByZip.name}
              </span>
              {#if selectedBusinessType}
                <span>{selectedBusinessType.name}</span>
              {/if}
            </div>
          {/if}
        </li>
        <li class="nav-item" role="presentation" class:d-none={isContainerDetailsTabHidden}>
          <button
            bind:this={containerDetailsTabEl}
            class="nav-link"
            id="tab3"
            data-bs-target="#tabPane3"
            type="button"
            role="tab"
            aria-controls="tabPane3"
            aria-selected="false"
            ><span class="step-circle">3<span class="step-circle-dot">.</span></span>
            {tabThreeLabel}
          </button>
          {#if shownTabEl !== null && !isInEditMode}
            <button class="btn-edit btn-reset text-primary" on:click={() => changeEditModeAndTab(containerDetailsTabEl, 3)}>{editLabel}</button>
          {/if}
          {#if selectedWasteType !== null || selectedFrequency != null}
            <div class="selected-tab-data mt-2">
              {#if selectedWasteType}
                <span>
                  {selectedWasteType.description}
                </span>
              {/if}
              {#if selectedFrequency}
                <span>
                  {selectedFrequency.display}
                </span>
              {/if}
              {#if isContainerDetailsNextValid && litePickerRolloff_SelectedDatesFormatted.length}
                <span>
                  {litePickerRolloff_SelectedDatesFormatted}
                </span>
              {/if}
            </div>
          {/if}
        </li>
      </ul>
      <div class="tab-content" id="myTabContent">
        <div class="tab-pane tab-pane--location fade show active" id="tabPane1" role="tabpanel" aria-labelledby="tab1">
          <div class="find-service-form__search-wrapper">
            <p class="find-service-form__search-label fw-bold">
              {searchSubTitle}
            </p>

            <div class="location-typeahead-wrapper">
              <LocationTypeahead
                bind:isInputClear
                bind:this={locationTypeaheadChild}
                placeholder={searchPlaceholder}
                {invalidAddressMessage}
                {apiGetSearchAddress}
                {apiGetSearchAddressReverse}
                labelFloatInside={false}
                bind:selectedSearchAddressResult={selectedResult}
                on:validChange={retrieveValidityUpdate}
              />
              <div class="find-service-form__search-btn-wrapper">
                <button
                  class="btn btn-primary btn-next find-service-form__search-btn"
                  disabled={!isLocationInputValid || isInputClear}
                  on:click={() => handleClickLocationNext()}
                >
                  {searchButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="tab-pane tab-pane--service fade" id="tabPane2" role="tabpanel" aria-labelledby="tab2">
          {#if isInEditMode}
            <div class="service-select-wrapper">
              <section class="service-select">
                <p class="service-select__title">
                  {selectServiceTitle}
                </p>
                <ul class="list-unstyled btn-select-list">
                  {#if serviceTypesByZip !== null}
                    {#each serviceTypesByZip as serviceType (serviceType.serviceTypeId)}
                      <li>
                        <button
                          class="btn btn-outline-primary btn-normal btn-radius-sm"
                          class:btn-selected={serviceType.isSelected}
                          on:click|stopPropagation={() => handleClickSelectService(serviceType)}
                        >
                          {@html serviceType.name.replaceAll("-", "&#8209;")}
                        </button>
                      </li>
                    {/each}
                  {/if}
                </ul>
              </section>
              <section
                class="service-select-next"
                class:service-select-next--only-btn={selectedServiceTypeByZip == null || selectedServiceTypeByZip.businessTypes.length === 0}
              >
                <p class="fw-bold" class:invisible={!selectedServiceTypeByZip?.businessTypes.length}>
                  {businessTypeTitle}
                </p>

                <div class="service-select-next__controls">
                  {#if selectedServiceTypeByZip?.businessTypes.length}
                    <div class="selection-wrapper selection-wrapper--chevron selection-wrapper--float-label">
                      <label for="selectBusinessTypes">{selectedServiceTypeByZip.lookupLabelBusinessType}</label>
                      <Select
                        items={selectedServiceTypeByZip.businessTypes.map((bt) => ({ ...bt, label: bt.name, value: bt.businessTypeId }))}
                        bind:value={selectedBusinessType}
                        listPlacement="bottom"
                        listOffset={0}
                        showChevron={true}
                        ChevronIcon={AtomicIconDropdown}
                        isClearable={false}
                        isSearchable={false}
                      />
                    </div>
                  {/if}
                  <button
                    class="btn btn-primary btn-next"
                    class:d-none={serviceTypesByZip !== null && !serviceTypesByZip.some((s) => s.isSelected)}
                    on:click={() => handleClickSelectServiceNext()}
                  >
                    {nextButtonLabel}
                  </button>
                </div>
              </section>
            </div>
          {/if}

          <SearchStatusMessage
            isHidden={pricingSearchResponse == null || (pricingSearchResponse !== null && searchStatusMessageHidden)}
            message={pricingSearchResponse?.searchStatusMessage}
          />
        </div>

        <div class="tab-pane tab-pane--container-details fade" id="tabPane3" role="tabpanel" aria-labelledby="tab3">
          <p class="container-detail__title" class:d-none={!isInEditMode}>
            {containerDetailsTitle}
          </p>
          <section class="container-detail-options" class:d-none={!isInEditMode}>
            <!-- Waste Type -->
            {#if selectedServiceTypeByZip?.wasteTypes.length}
              <div class="selection-wrapper selection-wrapper--chevron selection-wrapper--float-label">
                <label for="selectBusinessTypes">{selectedServiceTypeByZip.lookupLabelWasteType}</label>
                <Select
                  items={selectedServiceTypeByZip.wasteTypes.map((wt) => ({ ...wt, label: wt.description, value: wt.materialTypeId }))}
                  bind:value={selectedWasteType}
                  on:change={handleWasteTypeChange}
                  placeholder="Please select a waste type"
                  listPlacement="bottom"
                  listOffset={0}
                  showChevron={true}
                  ChevronIcon={AtomicIconDropdown}
                  isClearable={false}
                  isSearchable={false}
                />
              </div>
            {/if}

            <!-- Frequency -->
            {#if selectedWasteType?.frequencies.length}
              <div class="selection-wrapper selection-wrapper--chevron selection-wrapper--float-label">
                <label for="selectBusinessTypes">{selectedServiceTypeByZip.lookupLabelFrequency}</label>
                <Select
                  items={selectedWasteType.frequencies.map((frequency) => ({ ...frequency, label: frequency.display, value: frequency.serviceFrequencyId }))}
                  value={selectedFrequency}
                  on:change={handleFrequencyChange}
                  placeholder="Please select frequency"
                  listPlacement="bottom"
                  listOffset={0}
                  showChevron={true}
                  ChevronIcon={AtomicIconDropdown}
                  isClearable={false}
                  isSearchable={false}
                />
              </div>
            {/if}

            <!-- Date Picker -->
            <div
              class="input-group input-group--float-label input-group--datepicker"
              class:d-none={selectedServiceTypeByZip === null || !selectedServiceTypeByZip.enableDateRange}
            >
              <i class="bi bi-calendar4-week" />
              <input
                bind:this={litePickerRollOffEl}
                type="text"
                id="dateRangePicker"
                name="dateRangePicker"
                class="form-control form-control-xl"
                placeholder="MM/DD/YYYY — MM/DD/YYYY"
                autocomplete="off"
              />
              <label for="dateRangePicker">{selectedServiceTypeByZip?.lookupLabelDeliveryAndPickupDates}</label>
            </div>

            <button class="btn btn-primary btn-next"
                    disabled={!((isContainerDetailsNextValid || selectedFrequency) && selectedWasteType)}
                    on:click={() => handleClickContainerDetailsNext()}>
              {nextButtonLabel}
            </button>
          </section>

          <SearchStatusMessage
            isHidden={pricingSearchResponse == null || (pricingSearchResponse !== null && searchStatusMessageHidden)}
            message={pricingSearchResponse?.searchStatusMessage}
          />
        </div>
      </div>
    {/if}
  </div>
</div>

{#if !searchRedirect.length}
  <div class="find-service-results">
    {#if pricingSearchResponse === null && (serviceTypesByZip === null || serviceTypesByZip.length > 0)}
      <!-- pre results -->
      <section class="find-service-results__wrapper find-service-results__wrapper--initial">
        <div class="container">
          {#if preSearchIconSrc.length}
            <img src={preSearchIconSrc} class="img-fluid" alt="you are a few steps away" />
          {/if}

          <h4>{preSearchTitle}</h4>
          <p>
            {preSearchDescription}
          </p>
        </div>
      </section>
    {:else if pricingSearchResponse !== null && pricingSearchResponse.results?.length && !isInEditMode}
      <!-- results loaded -->
      <section class="find-service-results__wrapper find-service-results__wrapper--loaded">
        <div class="container">
          {#if hasFacets}
            <!-- Mobile Facet Filter Button -->
            <button class="btn-facets-filter btn-reset" on:click={(_) => hasFacetsOpen.set(true)}>
              <FacetFilterIcon />
              <span>{mobileFilterServicesLabel}</span>
            </button>
          {/if}

          <!-- Facets -->
          <GAQFacetList results={pricingSearchResponse.results} bind:selectedFacetValueIds bind:hasFacets />

          <div class="result__container">
            <ul class="cards-list list-unstyled">
              {#each pricingSearchResponse.results.filter((r) => selectedFacetValueIds.length === 0 || r.facets.some((f) => f.facetValues
                        .filter((fv) => selectedFacetValueIds.includes(fv.id)).length > 0)) as result (result)}
                <li class="cards-item">
                  <div class="card card--horizontal">
                    <div class="card-image">
                      <img src={result.image} alt="social post primary" class="img-fluid" />
                    </div>
                    <div class="card-body">
                      <div class="card-body-content">
                        <h4 class="card-title">{result.title}</h4>
                        <div class="card-price-wrapper">
                          <div class="rich-text">
                            {#if result.subtitle}
                              <p><strong>{result.subtitle}</strong></p>
                            {/if}
                            {@html result.description}
                          </div>

                          <div class="card-price">
                            {#if result.price != null}
                              <!-- account not found: show pricing -->
                              <p class="card-price-label">
                                {formatProductPricingLabel(selectedServiceTypeByZip.pricingLabel, result)}
                              </p>
                              <p class="card-price-amount">
                                {@html formatCardPrice(result.price)}
                              </p>
                            {:else}
                              <!-- no pricing available -->
                              <p class="card-price-unavailable">
                                {pricingSearchResponse.cardNoPricingMessage}
                              </p>
                            {/if}
                            {#if selectedServiceTypeByZip.pricingDescription}
                              <p class="card-price-description">
                                {formatProductPricingDescription(selectedServiceTypeByZip.pricingDescription, result)}
                              </p>
                            {/if}
                          </div>
                        </div>
                      </div>
                      <div class="card-actions">
                        <button
                          class="btn btn-primary btn-gaq-product-select-service"
                          data-title={result.title}
                          data-gtm-element-type="select-service-button"
                          on:click={(event) => handleClickSelectServiceFromCard(result)}>{pricingSearchResponse.cardLabelSelectService}
                        </button>
                        <a href={result.viewDetailsUrl}
                           target="_blank"
                           data-gtm-element-type="select-service-link"
                           on:mousedown={dispatchGTMServiceCtaLinkEvent}
                        >{pricingSearchResponse.cardLabelViewDetails}</a>
                      </div>
                    </div>
                  </div>
                </li>
              {/each}
            </ul>

            <div class="result-disclaimer">
              {@html pricingSearchResponse.resultsDisclaimer}
            </div>
          </div>
        </div>
      </section>
    {/if}
  </div>
{/if}

<!-- Pricing Submit Modal -->
{#if !searchRedirect.length}
  <div
    bind:this={pricingSubmitModalEl}
    class="modal modal--gaq fade"
    id="pricingSubmitModal"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-labelledby="pricingSubmitLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered" class:modal-dialog-scrollable={isMobileViewport()}>
      <div class="modal-content position-relative">
        <div class="modal-header justify-content-end">
          <button type="button" class="bi bi-x btn-reset text-primary fs-1 lh-1 btn-close-absolute" data-bs-dismiss="modal" aria-label="Close" />
        </div>
        <div class="modal-body">
          <section class="title-area col-12">
            <h4 class="modal-title" id="pricingSubmitLabel">
              {pricingSearchResponse?.submissionModalTitle}
            </h4>
            <p>
              {pricingSearchResponse?.submissionModalDescription}
            </p>
          </section>

          {#if selectedServiceResult !== null}
            <div class="container-fluid">
              <div class="row">
                <div class="col-12 col-md-4">
                  <div class="card card--selected-service">
                    <div class="card-image">
                      <img src={selectedServiceResult.image} alt="social post primary" class="img-fluid" />
                    </div>
                    <div class="card-body">
                      <div class="card-body-content">
                        <h5 class="card-title">
                          {selectedServiceResult.title}
                        </h5>

                        <div class="card-price">
                          {#if selectedServiceResult.price != null}
                            <!-- account not found: show pricing -->
                            <p class="card-price-label">
                              {formatProductPricingLabel(selectedServiceTypeByZip.pricingLabel, selectedServiceResult)}
                            </p>
                            <p class="card-price-amount">
                              {@html formatCardPrice(selectedServiceResult.price, true)}
                            </p>
                          {:else}
                            <!-- no pricing available -->
                            <p class="card-price-unavailable">
                              {pricingSearchResponse.cardNoPricingMessage}
                            </p>
                          {/if}
                          {#if selectedServiceTypeByZip.pricingDescriptionModal}
                            <p class="card-price-description">
                              {formatProductPricingDescription(selectedServiceTypeByZip.pricingDescriptionModal, selectedServiceResult)}
                            </p>
                          {/if}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-8">
                  <form class="pricing-submit-form row g-3 needs-validation"
                    novalidate
                    data-gtm-element-type="request-a-quote-complete"
                    id="pricingSubmitModalId"
                    bind:this={pricingSubmitFormEl}
                    on:submit={handleSubmitPricingModal}>
                    <div class="Form__Title d-none">{pricingSearchResponse?.submissionModalTitle}</div>
                    <section class="form-section-location col-12 position-relative">
                      <span class="label">
                        {pricingSearchResponse?.siteLocationAddressLabel}
                      </span>
                      <p class="mb-0">
                        {$userAddress?.address.freeformAddress}
                      </p>
                      <button class="btn-reset text-primary" on:click|stopPropagation|preventDefault={restartFindService}>{editLabel}</button>
                    </section>

                    {#if selectedBusinessType}
                      <section class="form-section-btype col-12">
                        <p>
                          <span class="label">{pricingSearchResponse?.businessTypeLabel}: </span>
                          {selectedBusinessType.name}
                        </p>
                      </section>
                    {/if}

                    <div class="input-group input-group--float-label has-validation">
                      <input type="text" class="form-control form-control-xl" id="fullName" required bind:value={pricingFormInputName} />
                      <label for="fullName">{pricingSearchResponse?.nameInputLabel}</label>
                    </div>

                    <div class="input-group input-group--float-label">
                      <input type="email" class="form-control form-control-xl" id="email" required bind:value={pricingFormInputEmail} />
                      <label for="email">{pricingSearchResponse?.emailInputLabel}</label>
                    </div>

                    <div class="input-group input-group--float-label">
                      <input type="tel" class="form-control form-control-xl" id="phone" required bind:value={pricingFormInputPhone} />
                      <label for="phone">{pricingSearchResponse?.phoneInputLabel}</label>
                    </div>

                    <div class="input-group input-group--float-label has-validation">
                      <input type="text" class="form-control form-control-xl" id="couponCode" bind:value={pricingFormInputCouponCode} />
                      <label for="couponCode">{pricingSearchResponse?.couponCodeInputLabel}</label>
                    </div>

                    <div class="result-disclaimer">
                      {@html pricingSearchResponse.couponCodeDisclaimer}
                    </div>

                    <!-- container details section -->
                    <section class="container-details col-12">
                      <p class="container-details__title">
                        {pricingSearchResponse?.containerDetailsLabel}
                      </p>

                      <div
                        class="row container-details-inputs"
                        class:d-none={selectedServiceTypeByZip.maxContainerQuantity === 0 && selectedServiceTypeByZip.enableDateRange}
                      >
                        {#if selectedServiceTypeByZip.maxContainerQuantityz > 0}
                          <div class="col-12 col-lg-3">
                            <div class="selection-wrapper selection-wrapper--chevron selection-wrapper--float-label">
                              <label for="selectBusinessTypes">
                                {pricingSearchResponse?.quantityLabel}
                              </label>
                              <Select
                                items={Array.from(
                                  {
                                    length: selectedServiceTypeByZip.maxContainerQuantity,
                                  },
                                  (_, i) => (i + 1).toString()
                                )}
                                bind:value={selectedQuantity}
                                listPlacement="bottom"
                                listOffset={0}
                                showChevron={true}
                                ChevronIcon={AtomicIconDropdown}
                                isClearable={false}
                                isSearchable={false}
                                on:change={(e) => updatePricingInModal(e, null)} 
                              />
                            </div>
                          </div>
                        {/if}
                        <div class="col-12 col-lg-9" class:d-none={selectedServiceTypeByZip.enableDateRange}>
                          <div class="input-group input-group--float-label input-group--datepicker">
                            <i class="bi bi-calendar4-week" />
                            <input
                              bind:this={litePickerDeliveryDateEl}
                              type="text"
                              id="dateRangePickerDeliveryDate"
                              name="dateRangePickerDeliveryDate"
                              class="form-control form-control-xl"
                              placeholder="MM/DD/YYYY"
                              autocomplete="off"
                              required={!selectedServiceTypeByZip.enableDateRange}
                            />
                            <label for="dateRangePickerDeliveryDate">{pricingSearchResponse?.requestedDeliveryDateLabel}</label>
                          </div>
                        </div>
                      </div>

                      <!-- read only look ups -->
                      <section class="section-locked-info">
                        {#if selectedServiceResult.frequency != null}
                          <p>
                            <span class="label">
                              {selectedServiceTypeByZip.lookupLabelFrequency}
                            </span>: {selectedServiceResult.frequency.display}
                          </p>
                        {/if}
                        {#if selectedServiceResult.wasteType != null}
                          <p>
                            <span class="label">
                              {selectedServiceTypeByZip.lookupLabelWasteType}
                            </span>: {selectedServiceResult.wasteType.description}
                          </p>
                        {/if}
                        {#if selectedServiceTypeByZip.enableDateRange}
                          <p>
                            <span class="label">
                              {pricingSearchResponse?.deliveryAndPickupDatesLabel}
                            </span>: {litePickerRolloff_SelectedDatesFormatted}
                          </p>
                        {/if}
                      </section>
                    </section>

                    <section class="optional-checks col-12">
                      <!-- optional checkbox: interested in other services. -->
                      <div class="form-check">
                        <input type="checkbox" class="form-check-input" bind:checked={otherServicesCheckboxValue} id="otherServicesCheckbox" />
                        <label for="otherServicesCheckbox">
                          {pricingSearchResponse?.otherServicesInputLabel}
                        </label>
                      </div>
                    </section>

                    <!-- submit section-->
                    <section class="submit-section col-12">
                      <p>{pricingSearchResponse?.pricingSubjectToMessage}</p>

                      <!-- server-side error message -->
                      {#if pricingModalServerSideErrorMessage.length}
                        <div class="invalid-feedback d-block mb-3">
                          {pricingModalServerSideErrorMessage}
                        </div>
                      {/if}
                      <input type="submit"
                             class="btn btn-primary"
                             class:btn-loading--dual-ring={isLoading}
                             disabled={isSubmitDisabled}
                             value={pricingSearchResponse?.submitButtonLabel}
                      />
                    </section>
                  </form>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </div>
    </div>
  </div>
{/if}
